import type { CSSProperties, ReactNode } from 'react'

import { cn } from '@/components/base'

/** The shape, where the element has to be something else — a router `Link`. */
export const PRESSABLE_ROW_CLASS =
  'flex w-full flex-row items-center gap-3 text-left'

/**
 * A whole row or card that is one press.
 *
 * Not a `Button`: it is as wide as the list it sits in, its content is
 * left-aligned, and it has no button ground of its own. Six screens wrote it
 * out and each had to excuse itself from the "no styled `<button>`" rule —
 * which is what a missing component looks like.
 *
 * The ground stays with the caller: Internal tints with a token, the editorial
 * areas with a literal grey, and a card lifts instead of tinting. What is
 * shared is the shape, not the surface.
 */
export function PressableRow({
  onPress,
  keepFocus = false,
  className,
  style,
  children,
}: {
  onPress: () => void
  /** The press leaves focus where it is — a row in a panel beside an editor
   *  acts on that editor's caret, which a focus change would move. */
  keepFocus?: boolean
  className?: string
  /** For a measure a class cannot carry — a tree indent counted in levels. */
  style?: CSSProperties
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      onMouseDown={keepFocus ? event => event.preventDefault() : undefined}
      style={style}
      className={cn(PRESSABLE_ROW_CLASS, className)}
    >
      {children}
    </button>
  )
}
